import type { ReactNode } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material'

interface RecordDialogProps {
  title: ReactNode
  children: ReactNode
  confirmLabel: string
  /** Renders the confirm button in the error colour, for a removal. */
  destructive?: boolean
  /** The action is in flight, so the confirm is held. */
  busy: boolean
  /** The body is not yet a request worth sending. */
  incomplete?: boolean
  /** Consequence of confirming, shown before the body — for a write members see straight away. */
  notice?: string
  refusal?: string
  /** Prefix for this dialog's test ids: `-notice`, `-refusal`, `-cancel` and `-confirm`. */
  testId: string
  maxWidth?: 'xs' | 'sm'
  onClose: () => void
  onConfirm: () => void
}

/**
 * The chrome every record dialog on this screen shares.
 *
 * The four of them — add or edit a document, remove one, add or edit a benefit info, remove one —
 * differ only in their body and their copy. Everything around it is the same: the title, the
 * server's reason if the last attempt was refused, and a cancel beside a confirm that is held while
 * the request is in flight. That refusal placement is the part worth sharing rather than
 * re-deciding: a dialog that closed on failure would hide the answer behind the backdrop.
 *
 * Rendered only while the dialog is up — none of these is kept mounted, because each is seeded from
 * the row that was clicked and one left mounted would reopen on the previous row's half-typed text.
 */
export const RecordDialog = ({
  title,
  children,
  confirmLabel,
  destructive = false,
  busy,
  incomplete = false,
  notice,
  refusal,
  testId,
  maxWidth = 'sm',
  onClose,
  onConfirm,
}: RecordDialogProps) => (
  <Dialog open onClose={onClose} fullWidth maxWidth={maxWidth}>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        {notice && (
          <Alert severity="warning" data-testid={`${testId}-notice`}>
            {notice}
          </Alert>
        )}
        {children}
        {refusal && (
          <Alert severity="error" data-testid={`${testId}-refusal`}>
            {refusal}
          </Alert>
        )}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} data-testid={`${testId}-cancel`}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color={destructive ? 'error' : 'primary'}
        onClick={onConfirm}
        disabled={busy || incomplete}
        data-testid={`${testId}-confirm`}
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
)
